import * as React from 'react';
import { useRef } from 'react';

import SpotlightCollectionModuleDelegator from 'bundles/front-page/components/SpotlightCollectionModuleDelegator';
import type { Modules } from 'bundles/front-page/hooks/usePageContentQuery';
import FrontPageCannedCollectionRendererDelegator from 'bundles/front-page/redesign-modules/FrontPageCannedCollectionRendererDelegator';
import OnboardingModuleRenderer from 'bundles/front-page/redesign-modules/OnboardingModuleRenderer';
import CareerAcademyModuleRedesignModuleRenderer from 'bundles/front-page/redesign-modules/career-academy/CareerAcademyModuleRedesignModuleRenderer';
import CompactCollectionsModuleRenderer from 'bundles/front-page/redesign-modules/compact-collections/CompactCollectionsModuleRenderer';
import DomainsModuleRenderer from 'bundles/front-page/redesign-modules/domains/DomainsModuleRenderer';
import FaqModuleRenderer from 'bundles/front-page/redesign-modules/faq/FaqModuleRenderer';
import HeroBannerModuleRenderer from 'bundles/front-page/redesign-modules/hero-banner/HeroBannerModuleRenderer';
import PartnerLogosModuleRenderer from 'bundles/front-page/redesign-modules/partner-logos/PartnerLogosModuleRenderer';
import TestimonialsModuleRenderer from 'bundles/front-page/redesign-modules/testimonials/TestimonialsModuleRenderer';

type Props = {
  modules: Modules;
};

const FrontPageModulesRenderer = ({ modules }: Props) => {
  const spotlightCollectionModuleCountRef = useRef<number>(0);
  const compactCollectionsCountRef = useRef<number>(0);
  return (
    <>
      {modules.map((module) => {
        switch (module.__typename) {
          case 'HeroBentoModule':
            return <HeroBannerModuleRenderer key={module.sys.id} content={module} />;
          case 'PromoTemplatePartnerList':
            return <PartnerLogosModuleRenderer key={module.sys.id} content={module} />;
          case 'LohpDomainModule':
            return <DomainsModuleRenderer key={module.sys.id} content={module} />;
          case 'LohpCareerAcademyModule':
            return <CareerAcademyModuleRedesignModuleRenderer key={module.sys.id} content={module} />;
          case 'LohpOnboardingModule':
            return <OnboardingModuleRenderer key={module.sys.id} />;
          case 'PromoTemplateTestimonialCarousel':
            return <TestimonialsModuleRenderer key={module.sys.id} content={module} />;
          case 'PromoTemplateCannedCollection':
            return <FrontPageCannedCollectionRendererDelegator key={module.sys.id} content={module} />;
          case 'CompactCollections': {
            compactCollectionsCountRef.current += 1;
            return (
              <CompactCollectionsModuleRenderer
                key={module.sys.id}
                content={module}
                dataTestId={`compact-collections-${compactCollectionsCountRef.current}`}
              />
            );
          }
          case 'PromoTemplateCollections': {
            spotlightCollectionModuleCountRef.current += 1;
            return (
              <SpotlightCollectionModuleDelegator
                key={module.sys.id}
                content={module}
                isFirstSpotlightCollectionModule={spotlightCollectionModuleCountRef.current === 1}
                dataTestId={`spotlight-collections-${spotlightCollectionModuleCountRef.current}`}
              />
            );
          }
          case 'PromoTemplateFaqs':
            return <FaqModuleRenderer key={module.sys.id} content={module} />;
          default:
            return null;
        }
      })}
    </>
  );
};

export default FrontPageModulesRenderer;
